import React, { useState } from 'react';
import { clientApprovalAPI } from '@/services/api';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Stack, CircularProgress, Box, Typography } from '@mui/material';
import { Button, confirmColors } from '@/components/ui';
import { useScope } from '@/hooks/useScope';
import { WRITE_CLIENT_MASTER } from '@/constants/scopeCodes';

interface ApprovalActionsProps {
  approvalId: string;
  onApprove: () => void;
  onReject: () => void;
}

const ApprovalActions: React.FC<ApprovalActionsProps> = ({ approvalId, onApprove, onReject }) => {
  const canWrite = useScope(WRITE_CLIENT_MASTER);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState('');

  if (!canWrite) {
    return (
      <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid var(--border)' }}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          You have read-only access. Contact an administrator to approve or reject this request.
        </Typography>
      </Box>
    );
  }

  const handleApprove = async () => {
    const result = await Swal.fire({
      title: 'Approve Request?',
      text: 'Please provide a reason for approval.',
      input: 'textarea',
      inputPlaceholder: 'Enter reason for approval...',
      icon: 'question',
      showCancelButton: true,
      ...confirmColors('success'),
      confirmButtonText: 'Yes, Approve',
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return 'A reason is required';
        }
        return null;
      },
    });

    if (!result.isConfirmed || !result.value) return;

    try {
      setLoading(true);
      await clientApprovalAPI.approveClient(parseInt(approvalId), {
        checker_comments: result.value.trim(),
      });
      toast.success('Request approved successfully');
      onApprove();
    } catch (err: any) {
      console.error('Error approving request:', err);
      const msg = err.response?.data?.error || err.response?.data?.detail || 'Failed to approve request';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    const { value: rejection_reason } = await Swal.fire({
      title: 'Reject Request',
      text: 'Please provide a reason for rejection',
      input: 'textarea',
      inputPlaceholder: 'Reason for rejection...',
      showCancelButton: true,
      ...confirmColors('danger'),
      confirmButtonText: 'Reject',
      inputValidator: (value) => {
        if (!value) {
          return 'Rejection reason is required!';
        }
        return null;
      }
    });

    if (rejection_reason) {
      try {
        setLoading(true);
        await clientApprovalAPI.rejectClient(parseInt(approvalId), {
          rejection_reason,
          checker_comments: comments
        });
        toast.success('Request rejected successfully');
        onReject();
      } catch (err: any) {
        console.error('Error rejecting request:', err);
        const msg = err.response?.data?.error || err.response?.data?.detail || 'Failed to reject request';
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Box sx={{ 
      mt: 4, 
      pt: 3, 
      borderTop: '1px solid var(--border)',
      display: 'flex',
      flexDirection: 'column',
      gap: 2
    }}>

      

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button
          onClick={handleReject}
          disabled={loading}
          variant="danger"
          size="lg"
          style={{ minWidth: 150 }}
        >
          Reject
        </Button>
        <Button
          onClick={handleApprove}
          disabled={loading}
          variant="success"
          size="lg"
          icon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          style={{ minWidth: 150 }}
        >
          Approve
        </Button>
      </Stack>
    </Box>
  );
};

export default ApprovalActions;
